import { 
  Directive, 
  ViewContainerRef, 
  OnInit, 
  OnChanges, 
  SimpleChanges,
  OnDestroy,
  ElementRef,
  Renderer2,
  input,
  TemplateRef,
  Type,
} from '@angular/core';

/**
 * interfaces
 */
import {
  ITableTemplateRenderer,
  ITableComponentRenderer,
  TableContentRendererUnion
} from '../../interfaces';

/**
 * enums
 */
import { TableContentRendererEnum } from '../../enums';

/**
 * utilities
 */
import { TableUtils } from '../../utilities';

/**
 * @directive TableContentRenderedDirective
 * @description Directive to handle rendering of table column content (headers/body cells)
 * Supports string, HTMLElement, TemplateRef, and Component rendering
 */
@Directive({
  selector: '[appTableContentRendered]',
})
export class TableContentRenderedDirective implements OnInit, OnChanges, OnDestroy {

  /**
   * Renderer
   */
  public renderer = input.required<TableContentRendererUnion<unknown>>({
    alias: 'appTableContentRendered'
  });

  /**
   * Data context
   */
  public dataContext = input<object>();

  /**
   * 
   * @param renderer 
   * @param elementRef 
   * @param viewContainerRef 
   */
  constructor(
    private readonly _renderer2: Renderer2,
    private readonly _elementRef: ElementRef,
    private readonly _viewContainerRef: ViewContainerRef,
  ) {}


  /**
   * 
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (changes['renderer'] || changes['dataContext']) {
      this.clear();
      this._render(this.renderer(), this.dataContext());
    }
    
  }

  /**
   * 
   */
  ngOnInit(): void {}

  /**
   * Main render method
   */
  private _render(renderer: TableContentRendererUnion<unknown>, dataContext: unknown): void {
    
    // If no column, return
    if (renderer == null) {
      return;
    }


    // Get renderer info
    const rendererInfo = TableUtils.getRendererInfo(renderer, dataContext ?? {});
    
    // If no renderer info, return
    if (rendererInfo == null) {
      return;
    }

    // Render based on type
    switch (rendererInfo.type) {
      case TableContentRendererEnum.String:
        this._renderedString(rendererInfo.renderer.toString());
        break;

      case TableContentRendererEnum.HTMLElement:
        this._renderedHTMLElement(rendererInfo.renderer as HTMLElement);
        break;

      case TableContentRendererEnum.Template:
        this._renderedTemplate(rendererInfo.renderer as ITableTemplateRenderer);
        break;

      case TableContentRendererEnum.Type:
        this._renderedComponent(rendererInfo.renderer as ITableComponentRenderer);
        break;
    }
    
  }

  /**
   * Render string content
   */
  private _renderedString(content: string): void {
    
    // If content is not a string, return
    if (typeof content !== 'string') {
      return;
    }

    this._renderer2.setProperty(this._elementRef.nativeElement, 'innerHTML', content);
  }

  /**
   * Render HTMLElement content
   */
  private _renderedHTMLElement(element: HTMLElement): void {
    
    // If element is not an HTMLElement, return
    if (!(element instanceof HTMLElement)) {
      return;
    }

    // Clone the element to avoid issues with original element
    const clonedElement = element.cloneNode(true) as HTMLElement;
    this._renderer2.appendChild(this._elementRef.nativeElement, clonedElement);
  }

  /**
   * Render template content
   */
  private _renderedTemplate(renderer: ITableTemplateRenderer): void {
    
    // If no renderer, return
    if (typeof renderer !== 'object' || renderer == null) {
      return;
    }

    // If template is not a TemplateRef, return
    if (!(renderer.template instanceof TemplateRef)) {
      return;
    }

    // ES6 destructuring
    // get template and context
    const { template, context } = renderer;

    // Create embedded view with template
    const view = this._viewContainerRef.createEmbeddedView(template, context);
    
    // if root nodes is an array, append the first node to the element
    if (Array.isArray(view.rootNodes)) {

      // get the first node
      view.rootNodes.forEach(node => {
        if (node instanceof HTMLElement) {
          this._renderer2.appendChild(this._elementRef.nativeElement, node);
        }
      });

    }
    
  }

  /**
   * Render component content
   */
  private _renderedComponent(renderer: ITableComponentRenderer): void {

    // If no renderer, return
    if (typeof renderer !== 'object' || renderer == null) {
      return;
    }

    // If component is not a Component, return
    if (!(renderer.component instanceof Type)) {
      return;
    }
    
    // Create component
    const componentRef = this._viewContainerRef.createComponent(
      renderer.component
    );

    let inputs: Record<string, unknown> = {};

    if (typeof renderer.inputs === 'function') {
      inputs = renderer.inputs(this.dataContext());
    }
    else if (typeof renderer.inputs === 'object') {
      inputs = renderer.inputs;
    }

    // Set inputs to component
    Object.entries(inputs).forEach(args => {
      
      // ES6 destructuring
      const [key, value] = args;

      componentRef.setInput(key, value);
    });

    this._renderer2.appendChild(this._elementRef.nativeElement, componentRef.location.nativeElement);

  }

  /**
   * Clear the view container
   */
  private clear(): void {
    this._viewContainerRef.clear();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.clear();
  }

}
